/**
 * An advanced search upsell used on the Header's search bar.
 * Fades in/out when show prop is changed.
 * If user is already premium, links to adv search page.
 *
 * TODO: should the premium version still link to adv search?
 *   I thought we killed that and the adv search upgrade page
 */
import React, { useRef } from 'react';
import styled from 'astroturf/react';
import { CSSTransition } from 'react-transition-group';

import routes from 'js/core_pre_webpack/routes';
import { ButtonLink } from 'js/design_system/link/ButtonLink';

import { NavVariant } from 'js/header/FocusedNav/types';

// Self-contained popover styling. Previously lived in the global
// `header.scss` (`.search-bar-upsell-container`), which meant the popover
// rendered unframed/always-on anywhere `header.scss` wasn't loaded (e.g.
// Storybook). Keeping it in astroturf ships the styles with the component.
// `CSSTransition` (with no `classNames` prop) applies the unprefixed phase
// classes `enter`/`enter-active`/…/`exit-done` to this element; the
// `&:global(...)` rules below match those, preserving the original behaviour.
const Container = styled.div`
  position: absolute;
  top: 10px;
  right: -175px;
  z-index: 1060;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  width: 360px;
  height: 210px;
  display: none;
  flex-direction: column;
  justify-content: center;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  background-color: #fff;
  padding: 30px 40px;
  text-align: center;
  transition: opacity ease-in-out 300ms;

  &:before {
    position: absolute;
    content: '';
    right: 175px;
    top: -10px;
    border-right: 9px solid transparent;
    border-bottom: 9px solid rgba(0, 0, 0, 0.15);
    border-left: 9px solid transparent;
  }

  &:after {
    position: absolute;
    top: -9px;
    right: 175px;
    content: '';
    border-right: 9px solid transparent;
    border-bottom: 9px solid #fff;
    border-left: 9px solid transparent;
  }

  &:global(.enter),
  &:global(.enter-active),
  &:global(.enter-done),
  &:global(.exit),
  &:global(.exit-active) {
    display: flex;
  }

  &:global(.enter) {
    opacity: 0;
  }

  &:global(.enter-active),
  &:global(.enter-done) {
    opacity: 1;
  }

  &:global(.exit) {
    opacity: 1;
  }

  &:global(.exit-active) {
    opacity: 0;
  }

  &:global(.exit-done) {
    display: none;
  }
`;

const Title = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
  margin-bottom: 12px;
`;

const Body = styled.div`
  letter-spacing: normal;
  line-height: 1.3em;
`;

// The popover's CTA. Uses the design system `ButtonLink` (self-contained,
// themed, no Bootstrap dependency) rather than a bespoke button; the astroturf
// wrapper only positions it within the popover (centered, spaced below body).
const Cta = styled(ButtonLink)`
  align-self: center;
  margin-top: 16px;
`;

// Pre-branch popover frame, restored for the `control` arm. These styles used
// to live in the global header.scss (`.search-bar-upsell-container`), which this
// branch deleted when it moved them into the redesign `Container` above. Ported
// here (minus the global class, and without the redesign's `justify-content:
// center`) so the control popover frames + stays hidden until hover WITHOUT
// depending on header.scss being loaded — header.scss is not loaded in the
// FocusedNav context, which is why the bare className rendered unframed/always-on.
// `CSSTransition` (no `classNames` prop) applies the unprefixed phase classes the
// `&:global(...)` rules match. Delete with the losing arm.
const ControlContainer = styled.div`
  position: absolute;
  top: 10px;
  right: -175px;
  z-index: 1060;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  width: 360px;
  height: 210px;
  display: none;
  flex-direction: column;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  background-color: #fff;
  padding: 30px 40px;
  text-align: center;
  transition: opacity ease-in-out 300ms;

  &:before {
    position: absolute;
    content: '';
    right: 175px;
    top: -10px;
    border-right: 9px solid transparent;
    border-bottom: 9px solid rgba(0, 0, 0, 0.15);
    border-left: 9px solid transparent;
  }

  &:after {
    position: absolute;
    top: -9px;
    right: 175px;
    content: '';
    border-right: 9px solid transparent;
    border-bottom: 9px solid #fff;
    border-left: 9px solid transparent;
  }

  &:global(.enter),
  &:global(.enter-active),
  &:global(.enter-done),
  &:global(.exit),
  &:global(.exit-active) {
    display: flex;
  }

  &:global(.enter) {
    opacity: 0;
  }

  &:global(.enter-active),
  &:global(.enter-done) {
    opacity: 1;
  }

  &:global(.exit) {
    opacity: 1;
  }

  &:global(.exit-active) {
    opacity: 0;
  }

  &:global(.exit-done) {
    display: none;
  }
`;

// Pre-branch CTA button, restored for the control arm. Delete with the losing arm.
const CtaButton = styled.button`
  background-color: #0954d3;
  color: #fff;
  height: 36px;
  letter-spacing: 1.15px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  font-weight: bold;
  border-radius: 3px;
  text-align: center;
  margin-top: 30px;
  padding: 0 20px;
  text-transform: uppercase;
  align-self: center;

  &:hover,
  &:focus,
  &:active {
    color: #fff;
    text-decoration: none;
  }
`;

type Props = {
  show: boolean;
  isPremium?: boolean;
  navVariant?: NavVariant;
};

const SearchUpsell = ({
  show,
  isPremium = false,
  navVariant = 'redesign',
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const ctaHref = isPremium
    ? routes.advanced_search_path({
        feature: 'search',
        trigger: 'search-popover',
        from_navbar: true,
      })
    : routes.upgrade_path({
        feature: 'search',
        trigger: 'search-popover',
        from_navbar: true,
      });
  const ctaLabel = isPremium ? 'Start Advanced Search' : 'Learn More';

  if (navVariant === 'control') {
    return (
      <CSSTransition in={show} timeout={300} nodeRef={ref}>
        <ControlContainer ref={ref}>
          <Title style={{ marginBottom: 26 }}>ACADEMIA PREMIUM FEATURE</Title>
          <Body>
            Search the full text of over 47 million papers on Academia.
          </Body>
          <CtaButton as="a" href={ctaHref}>
            {ctaLabel}
          </CtaButton>
        </ControlContainer>
      </CSSTransition>
    );
  }

  return (
    <CSSTransition in={show} timeout={300} nodeRef={ref}>
      <Container ref={ref}>
        <Title>ACADEMIA PREMIUM FEATURE</Title>
        <Body>Search the full text of over 47 million papers on Academia.</Body>
        <Cta size="sm" href={ctaHref}>
          {ctaLabel}
        </Cta>
      </Container>
    </CSSTransition>
  );
};

export default SearchUpsell;
